import React, { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

import styles from "./OrderHistoryDetailModal.module.css";

import OrderModalItemList from "./OrderModalItemList";

import { CURRENCY_SYMBOL_MAP, formatPrice, DELIVERY, PICKUP } from "../../constants";

import { ReactComponent as CloseIcon } from "../../assets/images/close.svg";

import { useCreateRefund } from "containers/Restaurant/OrderHistory/services/refund-services";

const OrderHistoryDetailModal = (props) => {
  const [refundForm, setRefundForm] = useState({
    show: false,
    reason: "Requested by customer",
    type: "full",
    amount: "",
  });

  const { mutate: createRefund, isPending: isRefunding } = useCreateRefund(props.restaurantId);

  let { data } = props;

  const {
    totalAmount = 0,
    totalCartAmount,
    serviceCharge = 0,
    tip = 0,
    _idRestaurantTable = [],
    onlinePayemtInfo = [],
    transactionStatus = "",
    transactionId = "",
    remainingToPay = 0,
    discountAmount = 0,
  } = data || {};

  const refunds = onlinePayemtInfo?.[0]?.refunds || [];
  const paymentStatus = transactionStatus;
  const isFullRefund = refunds.some(r => r.isFullRefund === true);

  // Calculate total refunded and remaining refundable amount
  const refundedAmount = refunds.reduce((sum, r) => sum + (r.amount || 0), 0);
  const remainingAmount = Math.max(0, remainingToPay - refundedAmount);
  const currencySymbol = CURRENCY_SYMBOL_MAP?.[data?.price?.currency] || "";

  const tableNumber = props.tableList.find(
    (x) => x?._id === _idRestaurantTable?.[0]
  )?.name;

  const handleRefundClick = () => {
    setRefundForm((prev) => ({ ...prev, show: true }));
  };

  const handleCancelRefund = () => {
    setRefundForm({ show: false, reason: "Requested by customer", type: "full", amount: "" });
  };

  const handleConfirmRefund = () => {
    if (refundForm.reason.trim() && data?._id) {
      const payload = {
        reason: refundForm.reason.trim(),
      };
      if (refundForm.type === "partial" && refundForm.amount) {
        payload.amount = parseFloat(refundForm.amount);
      }

      createRefund(
        {
          orderId: data._id,
          payload,
        },
        {
          onSuccess: () => {
            setRefundForm({ show: false, reason: "Requested by customer", type: "full", amount: "" });
            props.onRefund?.();
          },
        }
      );
    }
  };

  const isRefundFormValid = () => {
    if (!refundForm.reason.trim()) return false;
    if (remainingAmount <= 0) return false;
    if (refundForm.type === "partial") {
      const amount = parseFloat(refundForm.amount);
      return !isNaN(amount) && amount > 0 && amount <= remainingAmount;
    }
    return true;
  };

  const handleClose = () => {
    setRefundForm({ show: false, reason: "Requested by customer", type: "full", amount: "" });
    props.onClose();
  };

  return (
    <Modal
      className={styles.modelView}
      open={!(props.data == null)}
      onClose={handleClose}
    >
      {props.data ? (
        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            width: { md: "40%", xs: "98%" },
            maxHeight: "85vh",
            borderRadius: 3,
            "&:focus": {
              outline: "none",
            },
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            className={styles.dataView}
            sx={{ backgroundColor: "#263238" }}
            id={"headerViewId"}
            px={3}
            pt={1.5}
            pb={2}
          >
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
                <Typography className={styles.orderIdText} sx={{ color: "#FFF" }}>
                  Order #{data.orderRef ?? data._id.toString().substr(-5)}
                </Typography>
                <Box
                  className={styles.orderTypeView}
                  sx={{
                    backgroundColor:
                      data.orderType === DELIVERY || data.orderType === PICKUP
                        ? "#960090"
                        : "#1D86FF",
                    py: 0.3,
                    px: 1.2,
                  }}
                >
                  <Typography
                    className={styles.orderTypeText}
                    sx={{ color: "#FFF", fontSize: "11px !important" }}
                  >
                    {[DELIVERY, PICKUP].includes(data.orderType)
                      ? "Online Order"
                      : data.orderType === 4
                        ? "Table Order"
                        : "Dine in"}
                  </Typography>
                </Box>
              </Box>
              <Typography
                className={styles.orderDateText}
                sx={{ color: "#FFF", opacity: 0.8 }}
              >
                {data.date}
              </Typography>
              <Typography className={styles.nameText} sx={{ color: "#FFF", mt: 0.5 }}>
                {data.name}
              </Typography>
              {tableNumber ? (
                <Typography
                  className={styles?.["tbl-num"]}
                  sx={{ opacity: 0.9, mt: 0.3 }}
                >
                  Table {tableNumber}
                </Typography>
              ) : null}
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, mt: 1.5 }}>
                <Typography className={styles.priceText} sx={{ color: "#FFF" }}>
                  {data.price.currency}: {formatPrice(data.price.value, data.price.currency)}
                </Typography>
                <Typography
                  className={styles?.["transaction-status"]}
                  sx={{
                    backgroundColor: paymentStatus === "REFUNDED"
                      ? "rgba(211, 47, 47, 0.2)"
                      : paymentStatus === "PARTIALLY_REFUNDED"
                      ? "rgba(255, 152, 0, 0.2)"
                      : paymentStatus === "PAID"
                      ? "rgba(76, 175, 80, 0.2)"
                      : "rgba(255, 255, 255, 0.15)",
                    px: 1,
                    py: 0.3,
                    borderRadius: 1,
                  }}
                >
                  {paymentStatus ?? "--"}
                </Typography>
              </Box>
              {(transactionId || onlinePayemtInfo?.[0]?.transactionId) ? (
                <Typography className={styles?.["transaction-id"]} sx={{ opacity: 0.7, mt: 0.5 }}>
                  Payment ID: {onlinePayemtInfo?.[0]?.transactionId ?? transactionId ?? "--"}
                </Typography>
              ) : null}
            </Box>
            <Box className={styles.headerRightView}>
              <Box
                onClick={handleClose}
                className={styles?.["close-icon"]}
                sx={{
                  p: 0.5,
                  borderRadius: "50%",
                  transition: "background-color 0.2s",
                  path: { fill: "#FFF" },
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                <CloseIcon width={28} height={28} />
              </Box>
            </Box>
          </Box>
          <Box
            className={styles.menuListView}
            sx={{
              flex: 1,
              overflowY: "auto",
              minHeight: 0,
            }}
          >
            <Typography
              px={3}
              mt={2}
              className={styles.orderItemText}
              sx={{ color: "#000" }}
            >
              Ordered Items
            </Typography>
            {data.cart.map((x, i) => {
              return (
                <OrderModalItemList
                  key={i}
                  item={x}
                  lastIndex={data.cart.length === i + 1}
                  page={"orderHistoryDetail"}
                />
              );
            })}

            <Box p={3}>
              <Box className={styles?.["bill-view"]}>
                <Typography className={styles?.["bill-label"]}>
                  Cart Items Subtotal
                </Typography>
                <Typography className={styles?.["bill-val"]}>
                  {currencySymbol} {totalCartAmount ? formatPrice(totalCartAmount, data?.price?.currency) : formatPrice(totalAmount, data?.price?.currency)}
                </Typography>
              </Box>

              {data.charges?.chargeItems?.map((item, index) => {
                return (
                  <Box key={index} className={styles?.["bill-view"]}>
                    <Typography className={styles?.["bill-label"]}>
                      {item.name}
                    </Typography>
                    <Typography className={styles?.["bill-val"]}>
                      {currencySymbol} {item.isPercentage ? formatPrice((totalCartAmount * (item.value / 100)), data?.price?.currency) : formatPrice(item.value, data?.price?.currency)}
                    </Typography>
                  </Box>
                );
              })}
              {tip > 0 && <Box className={styles?.["bill-view"]}>
                <Typography className={styles?.["bill-label"]}>
                  Tip
                </Typography>
                <Typography className={styles?.["bill-val"]}>
                  {currencySymbol} {formatPrice(tip, data?.price?.currency)}
                </Typography>
              </Box>}
            </Box>
          </Box>
          <Box
            sx={{
              backgroundColor: "#F5F5F5",
              borderTop: "1px solid #E0E0E0",
              p: 2,
              flexShrink: 0,
            }}
          >
            {refundForm.show ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {/* Refund Summary Box */}
                <Box sx={{
                  backgroundColor: "#E3F2FD",
                  borderRadius: 1,
                  p: 1.5,
                  border: "1px solid #90CAF9",
                }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography sx={{ fontFamily: "InterRegular", fontSize: "12px", color: "#424242" }}>
                      Order Total
                    </Typography>
                    <Typography sx={{ fontFamily: "InterMedium", fontSize: "12px", color: "#424242" }}>
                      {currencySymbol}{totalAmount.toFixed(2)}
                    </Typography>
                  </Box>
                  {discountAmount > 0 && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                      <Typography sx={{ fontFamily: "InterRegular", fontSize: "12px", color: "#F57C00" }}>
                        Discount Applied
                      </Typography>
                      <Typography sx={{ fontFamily: "InterMedium", fontSize: "12px", color: "#F57C00" }}>
                        -{currencySymbol}{discountAmount.toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                  {refundedAmount > 0 && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                      <Typography sx={{ fontFamily: "InterRegular", fontSize: "12px", color: "#C62828" }}>
                        Already Refunded
                      </Typography>
                      <Typography sx={{ fontFamily: "InterMedium", fontSize: "12px", color: "#C62828" }}>
                        -{currencySymbol}{refundedAmount.toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: "flex", justifyContent: "space-between", borderTop: (refundedAmount > 0 || discountAmount > 0) ? "1px solid #90CAF9" : "none", pt: (refundedAmount > 0 || discountAmount > 0) ? 0.5 : 0 }}>
                    <Typography sx={{ fontFamily: "InterSemiBold", fontSize: "12px", color: "#1565C0" }}>
                      Available to Refund
                    </Typography>
                    <Typography sx={{ fontFamily: "InterSemiBold", fontSize: "12px", color: "#1565C0" }}>
                      {currencySymbol}{remainingAmount.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", gap: 1 }}>
                  <Box
                    onClick={() => setRefundForm((prev) => ({ ...prev, type: "full" }))}
                    sx={{
                      flex: 1,
                      py: 1,
                      borderRadius: 1,
                      backgroundColor: refundForm.type === "full" ? "#148E20" : "transparent",
                      border: `1px solid ${refundForm.type === "full" ? "#148E20" : "#BDBDBD"}`,
                      cursor: "pointer",
                      textAlign: "center",
                      transition: "all 0.2s",
                    }}
                  >
                    <Typography sx={{
                      fontFamily: "InterMedium",
                      fontSize: "13px",
                      color: refundForm.type === "full" ? "#FFF" : "#616161"
                    }}>
                      Full ({currencySymbol}{remainingAmount.toFixed(2)})
                    </Typography>
                  </Box>
                  <Box
                    onClick={() => setRefundForm((prev) => ({ ...prev, type: "partial" }))}
                    sx={{
                      flex: 1,
                      py: 1,
                      borderRadius: 1,
                      backgroundColor: refundForm.type === "partial" ? "#148E20" : "transparent",
                      border: `1px solid ${refundForm.type === "partial" ? "#148E20" : "#BDBDBD"}`,
                      cursor: "pointer",
                      textAlign: "center",
                      transition: "all 0.2s",
                    }}
                  >
                    <Typography sx={{
                      fontFamily: "InterMedium",
                      fontSize: "13px",
                      color: refundForm.type === "partial" ? "#FFF" : "#616161"
                    }}>
                      Partial
                    </Typography>
                  </Box>
                </Box>

                {refundForm.type === "partial" && (
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    placeholder={`Amount (max ${currencySymbol}${remainingAmount.toFixed(2)})`}
                    value={refundForm.amount}
                    onChange={(e) => {
                      const value = e.target.value;
                      const numValue = parseFloat(value);
                      if (value === "" || (!isNaN(numValue) && numValue <= remainingAmount)) {
                        setRefundForm((prev) => ({ ...prev, amount: value }));
                      }
                    }}
                    inputProps={{ min: 0, max: remainingAmount, step: "0.01" }}
                    error={parseFloat(refundForm.amount) > remainingAmount}
                    helperText={parseFloat(refundForm.amount) > remainingAmount ? `Amount cannot exceed ${currencySymbol}${remainingAmount.toFixed(2)}` : ""}
                    sx={{
                      backgroundColor: "#FFF",
                      "& .MuiOutlinedInput-root": {
                        fontFamily: "InterRegular",
                        fontSize: "13px",
                        borderRadius: 1,
                      }
                    }}
                  />
                )}

                <Select
                  fullWidth
                  size="small"
                  value={refundForm.reason}
                  onChange={(e) => setRefundForm((prev) => ({ ...prev, reason: e.target.value }))}
                  sx={{
                    backgroundColor: "#FFF",
                    fontFamily: "InterRegular",
                    fontSize: "13px",
                    borderRadius: 1,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#E0E0E0",
                    },
                  }}
                >
                  <MenuItem value="Duplicate" sx={{ fontFamily: "InterRegular", fontSize: "13px" }}>
                    Duplicate
                  </MenuItem>
                  <MenuItem value="Fraudulent" sx={{ fontFamily: "InterRegular", fontSize: "13px" }}>
                    Fraudulent
                  </MenuItem>
                  <MenuItem value="Requested by customer" sx={{ fontFamily: "InterRegular", fontSize: "13px" }}>
                    Requested by customer
                  </MenuItem>
                  <MenuItem value="Other" sx={{ fontFamily: "InterRegular", fontSize: "13px" }}>
                    Other
                  </MenuItem>
                </Select>

                <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                  <Box
                    onClick={handleCancelRefund}
                    sx={{
                      flex: 1,
                      py: 1.2,
                      borderRadius: 1,
                      backgroundColor: "transparent",
                      border: "1px solid #9E9E9E",
                      cursor: "pointer",
                      textAlign: "center",
                      transition: "all 0.2s",
                      "&:hover": { backgroundColor: "#EEEEEE" },
                    }}
                  >
                    <Typography sx={{ fontFamily: "InterMedium", fontSize: "13px", color: "#616161" }}>
                      Cancel
                    </Typography>
                  </Box>
                  <Box
                    onClick={!isRefunding && isRefundFormValid() ? handleConfirmRefund : undefined}
                    sx={{
                      flex: 1,
                      py: 1.2,
                      borderRadius: 1,
                      backgroundColor: isRefundFormValid() && !isRefunding ? "#148E20" : "#A5D6A7",
                      cursor: isRefundFormValid() && !isRefunding ? "pointer" : "not-allowed",
                      textAlign: "center",
                      transition: "all 0.2s",
                      "&:hover": isRefundFormValid() && !isRefunding ? { backgroundColor: "#0E7A1A" } : {},
                    }}
                  >
                    {isRefunding ? (
                      <CircularProgress size={18} sx={{ color: "#FFF" }} />
                    ) : (
                      <Typography sx={{ fontFamily: "InterMedium", fontSize: "13px", color: "#FFF" }}>
                        Confirm Refund
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            ) : refunds.length > 0 ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {/* Refund Summary Header */}
                <Box sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  backgroundColor: "#FFEBEE",
                  borderRadius: 1,
                  px: 2,
                  py: 1,
                }}>
                  <Box>
                    <Typography sx={{ fontFamily: "InterSemiBold", fontSize: "13px", color: "#C62828" }}>
                      Total Refunded
                    </Typography>
                    <Typography sx={{ fontFamily: "InterMedium", fontSize: "11px", color: "#757575" }}>
                      {refunds.length} refund{refunds.length > 1 ? 's' : ''} issued
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography sx={{ fontFamily: "InterSemiBold", fontSize: "14px", color: "#C62828" }}>
                      {currencySymbol}{refundedAmount.toFixed(2)}
                    </Typography>
                    {remainingAmount > 0 && (
                      <Typography sx={{ fontFamily: "InterMedium", fontSize: "11px", color: "#1565C0" }}>
                        {currencySymbol}{remainingAmount.toFixed(2)} remaining
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Refund History List */}
                <Box sx={{ maxHeight: 150, overflowY: "auto", display: "flex", flexDirection: "column", gap: 1 }}>
                  {refunds.map((refundItem, index) => (
                    <Box key={refundItem.refundId || index} sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          backgroundColor: "#FAFAFA",
                          borderRadius: 1,
                          px: 2,
                          py: 0.8,
                          border: "1px solid #E0E0E0",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              backgroundColor: "#C62828",
                            }}
                          />
                          <Typography
                            sx={{
                              fontFamily: "InterMedium",
                              fontSize: "12px",
                              color: "#424242",
                            }}
                          >
                            {refundItem.isFullRefund ? "Full Refund" : "Partial Refund"}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontFamily: "InterSemiBold",
                            fontSize: "12px",
                            color: "#C62828",
                          }}
                        >
                          {currencySymbol}{refundItem.amount?.toFixed(2)}
                        </Typography>
                      </Box>
                      {refundItem.reason && (
                        <Box
                          sx={{
                            backgroundColor: "#FFF",
                            borderRadius: 1,
                            px: 2,
                            py: 0.5,
                            ml: 2,
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "InterRegular",
                              fontSize: "11px",
                              color: "#757575",
                            }}
                          >
                            {refundItem.reason}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>

                {/* Issue Additional Refund Button */}
                {!isFullRefund && remainingAmount > 0 && (paymentStatus === "PAID"  || paymentStatus === "PARTIALLY_REFUNDED") && (
                  <Box
                    onClick={handleRefundClick}
                    sx={{
                      py: 1.2,
                      borderRadius: 1.5,
                      backgroundColor: "#148E20",
                      cursor: 'pointer',
                      textAlign: "center",
                      transition: "all 0.2s",
                      "&:hover": { opacity: 0.9 },
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "InterSemiBold",
                        fontSize: "13px",
                        color: "#FFF",
                      }}
                    >
                      Issue Additional Refund ({currencySymbol}{remainingAmount.toFixed(2)} left)
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              (paymentStatus === "PAID" ) && (
                <Box
                  onClick={handleRefundClick}
                  sx={{
                    py: 1.5,
                    borderRadius: 1.5,
                    backgroundColor: "#148E20",
                    cursor: 'pointer',
                    textAlign: "center",
                    transition: "all 0.2s",
                    "&:hover": { opacity: 0.9 },
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "InterSemiBold",
                      fontSize: "14px",
                      color: "#FFF",
                    }}
                  >
                    Issue Refund
                  </Typography>
                </Box>
              )
            )}
          </Box>
        </Box>
      ) : (
        <Box></Box>
      )}
    </Modal>
  );
};

export default OrderHistoryDetailModal;
